// divider — default skin module
// Token disposition: pass-through (no overrides)
// NO external imports — plain JavaScript only

export const styles = `
  :host {
    display: block;
    width: 100%;
    box-sizing: border-box;
  }

  :host([orientation="vertical"]) {
    display: inline-flex;
    width: auto;
    height: 100%;
    align-self: stretch;
  }

  /* Spacing */
  :host([spacing="xs"]) { margin: var(--run-space-semantic-2xs) 0; }
  :host([spacing="sm"]) { margin: var(--run-space-semantic-xs) 0; }
  :host([spacing="md"]) { margin: var(--run-space-semantic-sm) 0; }
  :host([spacing="lg"]) { margin: var(--run-space-semantic-md) 0; }
  :host([spacing="xl"]) { margin: var(--run-space-semantic-lg) 0; }

  :host([orientation="vertical"][spacing="xs"]) { margin: 0 var(--run-space-semantic-2xs); }
  :host([orientation="vertical"][spacing="sm"]) { margin: 0 var(--run-space-semantic-xs); }
  :host([orientation="vertical"][spacing="md"]) { margin: 0 var(--run-space-semantic-sm); }
  :host([orientation="vertical"][spacing="lg"]) { margin: 0 var(--run-space-semantic-md); }
  :host([orientation="vertical"][spacing="xl"]) { margin: 0 var(--run-space-semantic-lg); }

  [part="base"] {
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 100%;
  }

  :host([orientation="vertical"]) [part="base"] {
    flex-direction: column;
    align-items: center;
    width: auto;
    height: 100%;
  }

  /* Line segments — solid variant (background fill for WCAG 1.4.11) */
  [part="line"] {
    flex: 1;
    background: var(--run-divider-color, var(--run-color-border-default));
    height: 1px;
    border: none;
  }

  :host([orientation="vertical"]) [part="line"] {
    flex: 1;
    width: 1px;
    height: auto;
    min-height: 0;
    background: var(--run-divider-color, var(--run-color-border-default));
  }

  /* Weight variants */
  :host([weight="medium"]) [part="line"] {
    height: 2px;
  }
  :host([weight="thick"]) [part="line"] {
    height: 2px;
  }
  :host([orientation="vertical"][weight="medium"]) [part="line"] {
    width: 2px;
    height: auto;
  }
  :host([orientation="vertical"][weight="thick"]) [part="line"] {
    width: 2px;
    height: auto;
  }

  /* Dashed variant */
  :host([variant="dashed"]) [part="line"] {
    background: none;
    border-top: 1px dashed var(--run-divider-color, var(--run-color-border-default));
    height: 0;
  }
  :host([variant="dashed"][weight="medium"]) [part="line"] {
    border-top-width: 2px;
  }
  :host([variant="dashed"][weight="thick"]) [part="line"] {
    border-top-width: 2px;
  }
  :host([orientation="vertical"][variant="dashed"]) [part="line"] {
    background: none;
    border-top: none;
    border-inline-start: 1px dashed var(--run-divider-color, var(--run-color-border-default));
    width: 0;
    height: auto;
    flex: 1;
  }
  :host([orientation="vertical"][variant="dashed"][weight="medium"]) [part="line"] {
    border-inline-start-width: 2px;
  }
  :host([orientation="vertical"][variant="dashed"][weight="thick"]) [part="line"] {
    border-inline-start-width: 2px;
  }

  /* Dotted variant */
  :host([variant="dotted"]) [part="line"] {
    background: none;
    border-top: 1px dotted var(--run-divider-color, var(--run-color-border-default));
    height: 0;
  }
  :host([variant="dotted"][weight="medium"]) [part="line"] {
    border-top-width: 2px;
  }
  :host([variant="dotted"][weight="thick"]) [part="line"] {
    border-top-width: 2px;
  }
  :host([orientation="vertical"][variant="dotted"]) [part="line"] {
    background: none;
    border-top: none;
    border-inline-start: 1px dotted var(--run-divider-color, var(--run-color-border-default));
    width: 0;
    height: auto;
    flex: 1;
  }
  :host([orientation="vertical"][variant="dotted"][weight="medium"]) [part="line"] {
    border-inline-start-width: 2px;
  }
  :host([orientation="vertical"][variant="dotted"][weight="thick"]) [part="line"] {
    border-inline-start-width: 2px;
  }

  /* Color variants */
  :host([color="subtle"]) [part="line"] {
    background: var(--run-divider-color, var(--run-color-border-subtle));
  }
  :host([color="subtle"][variant="dashed"]) [part="line"],
  :host([color="subtle"][variant="dotted"]) [part="line"] {
    background: none;
    border-color: var(--run-divider-color, var(--run-color-border-subtle));
  }
  :host([orientation="vertical"][color="subtle"][variant="dashed"]) [part="line"],
  :host([orientation="vertical"][color="subtle"][variant="dotted"]) [part="line"] {
    border-color: var(--run-divider-color, var(--run-color-border-subtle));
  }

  :host([color="strong"]) [part="line"] {
    background: var(--run-divider-color, var(--run-color-border-strong));
  }
  :host([color="strong"][variant="dashed"]) [part="line"],
  :host([color="strong"][variant="dotted"]) [part="line"] {
    background: none;
    border-color: var(--run-divider-color, var(--run-color-border-strong));
  }
  :host([orientation="vertical"][color="strong"][variant="dashed"]) [part="line"],
  :host([orientation="vertical"][color="strong"][variant="dotted"]) [part="line"] {
    border-color: var(--run-divider-color, var(--run-color-border-strong));
  }

  :host([color="accent"]) [part="line"] {
    background: var(--run-divider-color, var(--run-divider-accent-color, var(--run-color-intent-primary-base)));
  }
  :host([color="accent"][variant="dashed"]) [part="line"],
  :host([color="accent"][variant="dotted"]) [part="line"] {
    background: none;
    border-color: var(--run-divider-color, var(--run-divider-accent-color, var(--run-color-intent-primary-base)));
  }
  :host([orientation="vertical"][color="accent"][variant="dashed"]) [part="line"],
  :host([orientation="vertical"][color="accent"][variant="dotted"]) [part="line"] {
    border-color: var(--run-divider-color, var(--run-divider-accent-color, var(--run-color-intent-primary-base)));
  }

  /* Label */
  [part="label"] {
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    padding-inline: var(--run-space-semantic-xs);
    font-size: var(--run-font-size-sm);
    color: var(--run-color-text-secondary);
    white-space: nowrap;
  }

  :host([orientation="vertical"]) [part="label"] {
    padding-inline: 0;
    padding-block: var(--run-space-semantic-xs);
    writing-mode: vertical-lr;
  }

  /* Label position — horizontal */
  :host([label-position="start"]) [part="base"] {
    justify-content: flex-start;
  }
  :host([label-position="end"]) [part="base"] {
    justify-content: flex-end;
  }
`;

export function render(host) {
  const { orientation, variant, weight, color, spacing, labelPosition, _hasLabel } = host; // declared in MVA SkinModule.render contract

  if (_hasLabel) {
    host.shadowRoot.innerHTML = `
      <div part="base">
        <div part="line"></div>
        <span part="label"><slot></slot></span>
        <div part="line"></div>
      </div>
    `;
  } else {
    host.shadowRoot.innerHTML = `
      <div part="base">
        <div part="line"></div>
      </div>
    `;
  }
}